import { SNAPSHOT_RING } from "./limits.ts";
import { Component, type EntityState, type SnapshotRecord } from "./snapshot.ts";

/**
 * The client's held world state (PROTOCOL.md 5.3): the newest applied tick and its entities, plus the
 * views of the last `SNAPSHOT_RING` ticks, since a delta names the view it is based on: the newest from a
 * map server of protocol 1.4.9, and the one the client last acknowledged, older by the round trip, from a map server
 * before it. A delta applies to the held view of its `baseTick`, each entity starting from the newest
 * state the client holds for it, and becomes the newest view. Views are plain maps rebuilt per frame; this
 * runs on the client, not in the tick loop.
 */
export interface ClientView {
  tick: number;
  entities: Map<number, EntityState>;
  readonly held: Map<number, Map<number, EntityState>>;
}

/**
 * An empty view: tick 0 with no entities, held so the first delta (based on tick 0) applies.
 *
 * @returns The view.
 */
export function newClientView(): ClientView {
  const empty = new Map<number, EntityState>();
  return { tick: 0, entities: empty, held: new Map([[0, empty]]) };
}

/**
 * The flags of a record that is an entity's whole state, name and all: a spawn that carries the Name.
 */
const NAMED_SPAWN = Component.Spawn | Component.Name;

/**
 * A decoded state with the name the client already holds for its handle, for a record that carried no Name
 * component (ADR-067 decision 2).
 *
 * @param incoming The decoded state, whose name reads `NO_NAME`.
 * @param held The state held for the handle, or undefined when there is none.
 * @returns The state, named as the held one, or the decoded state itself when nothing is held.
 */
function keepName(incoming: EntityState, held: EntityState | undefined): EntityState {
  return held === undefined || held.name === incoming.name ? incoming : { ...incoming, name: held.name };
}

/**
 * Merges the components a record carries into a held state. A record without the Name keeps the held name,
 * a spawn among them: a keyframe or an entrant's record names an entity only where the client lacks its name.
 * A spawn is otherwise the whole state, with or without the Offsets and the Companion, which only a player's carries
 * and whose absence the decoder reads as 0.
 *
 * @param held State held, or undefined for a new entity.
 * @param flags Components present.
 * @param incoming Decoded values.
 * @returns The merged state.
 */
function merge(held: EntityState | undefined, flags: number, incoming: EntityState): EntityState {
  if (held === undefined || (flags & NAMED_SPAWN) === NAMED_SPAWN) {
    return incoming;
  }
  if ((flags & Component.Spawn) !== 0) {
    return keepName(incoming, held);
  }
  return {
    handle: held.handle,
    x: flags & Component.Position ? incoming.x : held.x,
    y: flags & Component.Position ? incoming.y : held.y,
    facing: flags & Component.FacingAnim ? incoming.facing : held.facing,
    anim: flags & Component.FacingAnim ? incoming.anim : held.anim,
    health: flags & Component.Health ? incoming.health : held.health,
    height: flags & Component.Height ? incoming.height : held.height,
    running: flags & Component.Height ? incoming.running === true : held.running === true,
    pvp: flags & Component.Pvp ? incoming.pvp : held.pvp,
    infamy: flags & Component.Infamy ? incoming.infamy : held.infamy,
    away: flags & Component.Infamy ? (incoming.away ?? 0) : (held.away ?? 0),
    badge: flags & Component.Infamy ? (incoming.badge ?? 0) : (held.badge ?? 0),
    buffs: flags & Component.Buffs ? incoming.buffs : held.buffs,
    kind: held.kind,
    appearance: held.appearance,
    level: flags & Component.Level ? incoming.level : held.level,
    offsetX: flags & Component.Offsets ? (incoming.offsetX ?? 0) : (held.offsetX ?? 0),
    offsetY: flags & Component.Offsets ? (incoming.offsetY ?? 0) : (held.offsetY ?? 0),
    companion: flags & Component.Companion ? (incoming.companion ?? 0) : (held.companion ?? 0),
    cloakCut: flags & Component.Companion ? (incoming.cloakCut ?? 0) : (held.cloakCut ?? 0),
    cloakPalette: flags & Component.Companion ? (incoming.cloakPalette ?? 0) : (held.cloakPalette ?? 0),
    name: flags & Component.Name ? incoming.name : held.name,
  };
}

/**
 * Applies a decoded snapshot: a keyframe becomes the newest view outright; a delta whose `baseTick` the
 * view holds is applied to that base and becomes the newest view; any other delta, or one older than the
 * newest view, is discarded so the client waits for the next keyframe. The base decides which entities
 * the view holds, and each keeps the state the newest view holds for it when there is one (PROTOCOL.md
 * 5.3): an entity the delta leaves out, as the server does at a quarter rate, or carries only in part,
 * never goes back to the older state of a base a map server before 1.4.9 chose by the client's ack. A
 * handle in the delta's removed or crossed list leaves the view (PROTOCOL.md 5.2); the page holds a crossed
 * one's figure itself (ADR-147 decision 15).
 *
 * A record without the Name keeps the name the client holds for its handle (ADR-067 decision 2). A keyframe
 * takes it from the newest view when that view is fewer than `SNAPSHOT_RING` ticks older: a handle rests that
 * long before reuse, so a handle held on both ticks is one entity. Under the transport's rules every periodic
 * keyframe follows the newest view directly. Only a frame lost without a report makes a longer gap: then an
 * entity that entered the view in the frames discarded has no name until one arrives, and after a gap of a
 * ring or more neither has any entity the keyframe does not name, never another entity's.
 *
 * @param view The client's view, updated in place.
 * @param record The decoded snapshot.
 * @returns True when applied.
 */
export function applySnapshot(view: ClientView, record: SnapshotRecord): boolean {
  if (record.tick <= view.tick) {
    return false;
  }
  let next: Map<number, EntityState>;
  if (record.keyframe) {
    const named = record.tick - view.tick < SNAPSHOT_RING ? view.entities : undefined;
    next = new Map();
    for (const entry of record.entities) {
      next.set(entry.handle, (entry.flags & Component.Name) !== 0 ? entry.state : keepName(entry.state, named?.get(entry.handle)));
    }
  } else {
    const base = view.held.get(record.baseTick);
    if (base === undefined) {
      return false;
    }
    const newest = view.entities;
    next = new Map();
    for (const [handle, state] of base) {
      next.set(handle, newest.get(handle) ?? state);
    }
    for (const entry of record.entities) {
      next.set(entry.handle, merge(next.get(entry.handle), entry.flags, entry.state));
    }
    for (const handle of record.removed) {
      next.delete(handle);
    }
    for (const handle of record.crossed) {
      next.delete(handle);
    }
  }
  view.tick = record.tick;
  view.entities = next;
  view.held.set(record.tick, next);
  for (const tick of view.held.keys()) {
    if (tick <= record.tick - SNAPSHOT_RING) {
      view.held.delete(tick);
    }
  }
  return true;
}
